import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { TOUR_STEPS, TOTAL_CALLOUTS, type TourStep, type TourHighlight, type TourView } from './steps';

interface TourContextValue {
  isOpen: boolean;
  stepIndex: number;
  highlightIndex: number;
  step: TourStep | null;
  highlight: TourHighlight | null;
  totalSteps: number;
  totalCallouts: number;
  calloutOrdinal: number;
  isFirst: boolean;
  isLast: boolean;
  // autoplay
  isPlaying: boolean;
  autoplayMs: number;
  setAutoplayMs: (ms: number) => void;
  play: () => void;
  pause: () => void;
  // navigation
  open: (atStepIndex?: number, atHighlightIndex?: number) => void;
  close: () => void;
  next: () => void;
  prev: () => void;
  restart: () => void;
  goToStep: (stepIndex: number, highlightIndex?: number) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

/**
 * The host app passes in callbacks the tour uses to move between screens,
 * so the tour stays decoupled from the app's routing/auth implementation.
 */
export interface TourHostBridge {
  ensureView: (view: TourView) => void | Promise<void>;
}

interface TourProviderProps {
  children: ReactNode;
  bridge: TourHostBridge;
  /** Initial autoplay interval in ms. Defaults to 15 seconds — longer
   *  than feels natural at a laptop, because booth visitors need time
   *  to (a) see the overview pan-in, then (b) read the callout copy. */
  defaultAutoplayMs?: number;
}

export function TourProvider({ children, bridge, defaultAutoplayMs = 15000 }: TourProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplayMs, setAutoplayMs] = useState(defaultAutoplayMs);

  const clampStep = (i: number) => Math.max(0, Math.min(i, TOUR_STEPS.length - 1));

  const open = useCallback((atStepIndex?: number, atHighlightIndex = 0) => {
    if (atStepIndex !== undefined) {
      const s = clampStep(atStepIndex);
      const step = TOUR_STEPS[s];
      setStepIndex(s);
      setHighlightIndex(Math.max(0, Math.min(atHighlightIndex, (step?.highlights.length ?? 1) - 1)));
    }
    setIsOpen(true);
  }, []);

  const restart = useCallback(() => {
    setStepIndex(0);
    setHighlightIndex(0);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsPlaying(false);
    try {
      // User explicitly closed — don't auto-resume on next reload.
      sessionStorage.removeItem('cc-tour-autoresume');
    } catch { /* noop */ }
  }, []);

  // Auto-resume after a loop-triggered reload. Set by `advance(loop=true)`
  // when the tour reaches step 20 with autoplay on; cleared by `close()`
  // (and naturally by the tab being closed, since this is sessionStorage).
  useEffect(() => {
    try {
      if (sessionStorage.getItem('cc-tour-autoresume') === '1') {
        sessionStorage.removeItem('cc-tour-autoresume');
        setStepIndex(0);
        setHighlightIndex(0);
        setIsOpen(true);
        setIsPlaying(true);
      }
    } catch { /* noop */ }
  }, []);

  const advance = useCallback((dir: 1 | -1, loop = false) => {
    setStepIndex((s) => {
      setHighlightIndex((h) => {
        const step = TOUR_STEPS[s];
        if (!step) return h;
        if (dir === 1) {
          if (h < step.highlights.length - 1) return h + 1;
          // move to next step
          if (s < TOUR_STEPS.length - 1) {
            queueMicrotask(() => setStepIndex(s + 1));
            return 0;
          }
          // at end — loop if autoplay. Reload the page instead of
          // reusing the in-memory state. Booth runs cycle through 20
          // steps over ~5–7 minutes; without a reset, cart state,
          // route state, and any in-flight scroll/measure timers from
          // earlier loops accumulate and cause visible scrolling
          // jitter on later cycles. Reloading is the simplest way to
          // guarantee each loop starts clean. A sessionStorage flag
          // is read on mount (see effect below) to auto-resume the
          // tour from step 1 with autoplay on, so the booth experience
          // is seamless.
          if (loop) {
            try {
              sessionStorage.setItem('cc-tour-autoresume', '1');
            } catch { /* noop */ }
            window.location.reload();
            return h;
          }
          return h;
        }
        // dir === -1
        if (h > 0) return h - 1;
        if (s > 0) {
          const prevStep = TOUR_STEPS[s - 1];
          queueMicrotask(() => setStepIndex(s - 1));
          return prevStep.highlights.length - 1;
        }
        return h;
      });
      return s;
    });
  }, []);

  const next = useCallback(() => advance(1, false), [advance]);
  const prev = useCallback(() => advance(-1, false), [advance]);

  const goToStep = useCallback((i: number, hi = 0) => {
    const s = clampStep(i);
    const step = TOUR_STEPS[s];
    setStepIndex(s);
    setHighlightIndex(Math.max(0, Math.min(hi, (step?.highlights.length ?? 1) - 1)));
  }, []);

  // Autoplay — advance at interval, loop at end.
  // Depends on stepIndex + highlightIndex so the interval restarts on any
  // manual navigation (otherwise next/prev races against a stale timer).
  const tickRef = useRef<number | null>(null);
  useEffect(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (!isOpen || !isPlaying) return;
    // Per-step override wins over the global slider value — useful for
    // steps with long internal followUp chains (step 7 needs ~25s).
    const stepNow = TOUR_STEPS[stepIndex];
    const effectiveMs = stepNow?.autoplayMs ?? autoplayMs;
    tickRef.current = window.setInterval(() => {
      advance(1, true);
    }, effectiveMs);
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isOpen, isPlaying, autoplayMs, advance, stepIndex, highlightIndex]);

  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);

  // When the active step changes, ask the host to align the view.
  const step = TOUR_STEPS[stepIndex] ?? null;
  useEffect(() => {
    if (!isOpen || !step) return;
    try {
      bridge.ensureView(step.view);
    } catch {
      /* swallow — keep tour moving */
    }
  }, [isOpen, step, bridge]);

  const highlight = step?.highlights[highlightIndex] ?? null;

  // Dispatch `cc-tour-trigger` on every step OR highlight change so
  // listeners can both *open* effects (modals, dialogs, expanded rows)
  // for the active callout and *close* whatever they opened previously
  // when the trigger goes back to undefined. Highlight-level trigger
  // wins when present so a single step can sequence "call out the
  // button → open the modal it controls → call out the modal."
  useEffect(() => {
    if (!isOpen || !step) return;
    const triggerName = highlight?.trigger ?? step.trigger;
    // Short delay so ensureView's route/auth state updates have time
    // to flush and the target component mounts before its trigger
    // listener is queried. Auth state in this app is synchronous
    // setState calls so 150ms is plenty — the original 400ms delayed
    // every step's first action visibly.
    const id = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('cc-tour-trigger', {
        detail: { trigger: triggerName, stepId: step.id },
      }));
    }, 150);
    return () => window.clearTimeout(id);
  }, [isOpen, stepIndex, highlightIndex, step, highlight]);

  const calloutOrdinal = useMemo(() => {
    let count = 0;
    for (let i = 0; i < stepIndex; i++) count += TOUR_STEPS[i].highlights.length;
    return count + highlightIndex + 1;
  }, [stepIndex, highlightIndex]);

  const isFirst = stepIndex === 0 && highlightIndex === 0;
  const isLast =
    stepIndex === TOUR_STEPS.length - 1 &&
    step !== null &&
    highlightIndex === step.highlights.length - 1;

  const value = useMemo<TourContextValue>(
    () => ({
      isOpen,
      stepIndex,
      highlightIndex,
      step,
      highlight,
      totalSteps: TOUR_STEPS.length,
      totalCallouts: TOTAL_CALLOUTS,
      calloutOrdinal,
      isFirst,
      isLast,
      isPlaying,
      autoplayMs,
      setAutoplayMs,
      play,
      pause,
      open,
      close,
      next,
      prev,
      restart,
      goToStep,
    }),
    [
      isOpen, stepIndex, highlightIndex, step, highlight, calloutOrdinal, isFirst, isLast,
      isPlaying, autoplayMs, play, pause, open, close, next, prev, restart, goToStep,
    ],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}
